import { Layout } from '../components/Layout';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const WASTAGE_REASONS = [
  { value: 'damage', label: 'Damage' },
  { value: 'spoilage', label: 'Spoilage' },
  { value: 'overproduction', label: 'Overproduction' },
  { value: 'trial', label: 'Trial / Sampling' },
  { value: 'staff_error', label: 'Staff Error' },
];

export default function Wastage({ user, onLogout }) {
  const [wastageRecords, setWastageRecords] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({
    product_id: '',
    quantity: '',
    reason: 'damage',
    cost: ''
  });

  useEffect(() => {
    fetchWastageRecords();
    fetchProducts();
  }, []);

  const fetchWastageRecords = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await axios.get(`${API}/wastage?date=${today}`, getAuthHeader());
      setWastageRecords(res.data);
    } catch (error) {
      console.error('Failed to fetch wastage records:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`, getAuthHeader());
      setProducts(res.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const wastageData = {
      wastage_id: `wst_${Date.now()}`,
      product_id: formData.product_id,
      location_id: 'central_kitchen',
      quantity: parseFloat(formData.quantity),
      unit: 'pcs',
      reason: formData.reason,
      cost: parseFloat(formData.cost),
      reported_by: user.phone,
    };

    try {
      await axios.post(`${API}/wastage`, wastageData, getAuthHeader());
      toast.success('Wastage recorded successfully');
      setShowForm(false);
      resetForm();
      fetchWastageRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record wastage');
    }
  };

  const resetForm = () => {
    setFormData({
      product_id: '',
      quantity: '',
      reason: 'damage',
      cost: ''
    });
  };

  const getReasonLabel = (reason) => {
    const item = WASTAGE_REASONS.find(r => r.value === reason);
    return item?.label || reason;
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-heading text-4xl font-bold text-stone-900">Wastage Tracking</h1>
            <p className="text-stone-600 mt-1">Record and monitor wastage</p>
          </div>
          <Button
            data-testid="add-wastage-button"
            onClick={() => setShowForm(!showForm)}
            className="bg-secondary hover:bg-secondary-hover text-white"
          >
            <Plus size={20} className="mr-2" />
            Record Wastage
          </Button>
        </div>

        {showForm && (
          <div className="bg-white border border-stone-200 rounded-sm shadow-sm p-6 mb-8">
            <h2 className="font-heading text-2xl font-semibold text-stone-800 mb-6">Record Wastage</h2>
            
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Product</label>
                  <Select value={formData.product_id} onValueChange={(value) => setFormData({ ...formData, product_id: value })}>
                    <SelectTrigger data-testid="product-select">
                      <SelectValue placeholder="Select product" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map(p => (
                        <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Reason</label>
                  <Select value={formData.reason} onValueChange={(value) => setFormData({ ...formData, reason: value })}>
                    <SelectTrigger data-testid="reason-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {WASTAGE_REASONS.map(r => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Quantity</label>
                  <input
                    type="number"
                    data-testid="quantity-input"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    className="w-full h-10 px-3 border border-stone-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Cost (₹)</label>
                  <input
                    type="number"
                    data-testid="cost-input"
                    value={formData.cost}
                    onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                    className="w-full h-10 px-3 border border-stone-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button type="submit" data-testid="submit-wastage-button" className="bg-primary hover:bg-primary-hover text-white">
                  Record Wastage
                </Button>
                <Button type="button" onClick={() => setShowForm(false)} variant="outline">
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white border border-stone-200 rounded-sm shadow-sm">
          <div className="p-6 border-b border-stone-200">
            <h2 className="font-heading text-2xl font-semibold text-stone-800">Today's Wastage</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Wastage ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Product</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Quantity</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Reason</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Cost</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Reported By</th>
                </tr>
              </thead>
              <tbody>
                {wastageRecords.map((record) => (
                  <tr key={record.wastage_id} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="px-4 py-3 font-mono text-sm">{record.wastage_id}</td>
                    <td className="px-4 py-3">{record.product_id}</td>
                    <td className="px-4 py-3 font-mono">{record.quantity}</td>
                    <td className="px-4 py-3">{getReasonLabel(record.reason)}</td>
                    <td className="px-4 py-3 font-mono font-semibold text-functional-error">₹{record.cost.toFixed(2)}</td>
                    <td className="px-4 py-3 font-mono text-sm">{record.reported_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {wastageRecords.length === 0 && (
              <div className="text-center py-12 text-stone-400">No wastage recorded today</div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
